import type {
	PartnerBalanceTable,
	PartnerCards,
	PartnerDetail,
	PartnerMutations,
	PartnerSelectOptions,
	PartnerShared,
	PartnerStatistics,
	PartnerTabs,
	PartnerTranslations,
	ServiceFeeInvoice,
	ValidationMessages,
} from "Infrastructure/Translation/Types/partner";

const partnerTabsTranslations: PartnerTabs = {
	title: "Partner detail",
	description: "Detailed information about the partner",
	orders: "Orders",
	balance: "Balance",
	edit: "Edit",
	invoices: "Payment requests",
	detail: "Detail",
	availability: "Availability",
};

const partnerCardsTranslation: PartnerCards = {
	assigned: "Assigned parner",
	replaced: "Replacement partner",
};

const partnerDetailTranslations: PartnerDetail = {
	generalInfo: {
		id: "ID",
		title: "General Information",
		update: "Update",
		name: "Name",
		middleName: "Middle Name",
		surname: "Surname",
		phone: "Phone",
		email: "Email",
		isCanTrainDay: "Training Days",
		market: "Market",
		marketRegion: "Region",
		serviceAreas: "Operating cities",
		serviceAreaRegion: "City / region",
		serviceAreaOrigin: "Driving-distance origin address",
		primaryServiceArea: "Primary city",
		addServiceArea: "Add operating city",
		removeServiceArea: "Remove operating city",
		teamSize: "Job Capacity",
		status: "Status",
		marketName: "Market",
		invoiceIdentifier: "Payment request identifier",
		trainDaysFalse: "No",
		trainDaysTrue: "Yes",
		profileLink: "Profile Link",
		verticals: "Verticals",
		skills: "Skills",
		notes: "Notes",
		registerPartnerPortal: "Register in Partner Portal",
		registeringPartnerPortal: "Registering...",
	},
	bankAccount: {
		title: "Bank Information",
		update: "Update",
		iban: "IBAN",
		bic: "BIC/SWIFT",
		accountHolder: "Account holder",
	},
	addressInfo: {
		title: "Address Information",
		update: "Update",
		street: "Street",
		city: "City",
		builduingNumber: "Building Number",
		postCode: "Post Code",
		addressType: "Address Type",
		country: "Country",
	},
	billingProfile: {
		title: "Billing Profile",
		update: "Update",
		vatId: "VAT ID",
		vatLiable: "VAT Liable",
		invoicingName: "Invoicing Name",
		businessId: "Business ID",
		vatLiableTrue: "Yes",
		vatLiableFalse: "No",
	},
	create: {
		title: "Add partner",
		save: "Save",
	},
	score: {
		title: "Internal Score",
		internalScore: "Total Score",
		ratingScore: "Rating Score",
		acceptanceScore: "Acceptance Score",
		claimScore: "Claim Score",
		rejectionScore: "Rejection Score",
		rawRating: "Raw Rating",
		rawAcceptanceRate: "Raw Acceptance",
		rawClaimRate: "Raw Claim",
		rawRejectionRate: "Raw Rejection",
	},
};

const partnerMutationTranslations: PartnerMutations = {
	partnerCreateSuccess: "New partner created successfully",
	partnerCreateError: "Failed to create a new partner",
	partnerAddressUpdateSuccess: "Partner address updated successfully",
	partnerAddressUpdateError: "Failed to update partner address",
	partnerBankAccountUpdateSuccess: "Partner bank account updated successfully",
	partnerBankAccountUpdateError: "Failed to update partner bank account",
	partnerBillingProfileUpdateSuccess:
		"Partner billing profile updated successfully",
	partnerBillingProfileUpdateError: "Failed to update partner billing profile",
	partnerGeneralInfoUpdateSuccess:
		"Partner general information updated successfully",
	partnerGeneralInfoUpdateError: "Failed to update partner general information",
	partnerCreateBalanceTransactionSuccess:
		"Partner balance transaction created successfully",
	partnerCreateBalanceTransactionError:
		"Failed to create partner balance transaction",
};

const partnerBalanceTableTranslations: PartnerBalanceTable = {
	id: "ID",
	name: "Name",
	orderId: "Order ID",
	type: "Type",
	amount: "Amount",
	status: "Status",
	description: "Description",
	vat: "VAT",
	monthTag: "Month",
};

const partnerSharedTranslations: PartnerShared = {
	action: "Action",
	addNewPartner: "Add New Partner",
	heading: "Partners",
	orders: "Orders",
	phone: "Phone",
	email: "Email",
	filters: "Filters",
	query: "Search query",
	search: "Search",
	reset: "Reset",
	country: "Country",
	allMarkets: "All markets",
};

const validationMessagesTranslations: ValidationMessages = {
	partnerGeneral: {
		name: "Name is required and cannot be empty.",
		surname: "Surname is required and cannot be empty.",
		phone: "Phone number is required.",
		email: "Email is required.",
		teamSize: "The job capacity must be at least 1.",
		marketId: "Market ID must be an integer.",
		marketRegionId: "Market Region ID must be an integer.",
		phoneFormat: "Invalid phone number format.",
		emailFormat: "Invalid email format.",
		verticals: "At least one vertical is required.",
		serviceAreas: "At least one operating city is required.",
		serviceAreaOrigin:
			"An origin address is required for every operating city.",
	},
};

const partnerSelectOptionsTranslations: PartnerSelectOptions = {
	address: {
		BILLING: "Billing",
		PRIMARY: "Primary",
	},
	skills: {
		INTERIOR_PAINTING: "Interior Painting",
		INTERIOR_PAINTING_PREMIUM: "Interior Painting Premium",
		EXTERIOR_PAINTING: "Exterior Painting",
		WALLPAPERING: "Wallpapering",
		PLUMBING: "Plumbing",
		TILING: "Tiling",
		PLASTERBOARDS: "Plasterboards",
		PLASTERING: "Plastering",
		REWIRING: "Rewiring",
		REVISION: "Revision",
		PARQUET: "Parquet",
		PARQUET_RENO: "Parquet Reno",
		CARPET: "Carpet",
		LINO: "Lino",
		VINYL: "Vinyl",
		HANDYMAN: "Handyman",
	},
	status: {
		ACTIVE: "Active",
		DELETED: "Deleted",
		BLOCKED: "Blocked",
	},
};

const serviceFeeInvoicesTranslations: ServiceFeeInvoice = {
	table: {
		id: "ID",
		invoiceNumber: "Invoice Number",
		totalAmountWithoutVat: "Total Amount without VAT",
		vatAmount: "VAT Amount",
		totalAmountWithVat: "Total Amount with VAT",
		customerName: "Customer Name",
		supplierName: "Supplier Name",
		actions: "Actions",
	},
	form: {
		export: "Export",
		selectMonthTag: "Please select a month tag",
	},
	shared: {
		title: "Service Fee Invoices",
		description: "List of service fee invoices",
	},
};

const partnerStatisticsTranslations: PartnerStatistics = {
	partnerId: "Partner ID",
	partnerName: "Partner Name",
	openClaims: "Open Claims",
	totalClaims: "Total Claims",
	ordersDone: "Orders Done",
	ordersConfirmed: "Orders Confirmed",
	ordersInProgress: "Orders In Progress",
	ordersCancelled: "Orders Cancelled",
	ordersCancelledPercentage: "C-cancelations (%)",
	description: "Partner statistics",
	title: "Partner statistics",
	filter: {
		filters: "Filters",
		market: "Market",
		region: "Region",
		search: "Search",
		reset: "Reset",
	},
};

export const partnerTranslations: PartnerTranslations = {
	tabs: partnerTabsTranslations,
	detail: partnerDetailTranslations,
	mutations: partnerMutationTranslations,
	balanceTable: partnerBalanceTableTranslations,
	shared: partnerSharedTranslations,
	validationMessages: validationMessagesTranslations,
	selectOptions: partnerSelectOptionsTranslations,
	serviceFeeInvoice: serviceFeeInvoicesTranslations,
	statistics: partnerStatisticsTranslations,
	cards: partnerCardsTranslation,
};
